import { ALL_CUSTOM_SOURCES } from '@/lib/enums'
import { httpGetJson } from '@/lib/net'
import {
  INIT_LAYER_MANAGEMENT_START,
  INIT_LAYER_MANAGEMENT_STOP,
} from '@/lib/performance-marks'
import { saveItem } from '@/plugins/storage'

import { isActive } from '@/services/feature-flag-service'
import { getUserId } from '@/services/identity-client'
import {
  cleanupListeners,
  registerListeners,
} from '@/services/layer-management-service/listeners'
import { migrateBasemaps } from '@/services/layer-management-service/migration'
import {
  getSettings as getPersistedSettings,
  initializePersistence,
  saveSettings as savePersistedSettings,
  updateLayerSettings,
} from '@/services/layer-management-service/persistence'
import { resetStore, useStore } from '@/services/layer-management-service/store'
import { initStylesheet } from '@/services/layer-management-service/stylesheet'
import subs from '@/services/subscription-service'
import {
  LayerManager,
  Membership,
  StorageOption,
  type Api,
  type Config,
  type Source,
  type StorageRecords,
} from '@onxmaps/layer-management'
import { captureException } from '@sentry/vue'
import { computed, watch } from 'vue'

type Callback = () => void

let manager: LayerManager

let readyResolver: Callback
let readyPromise = new Promise<void>((resolve) => (readyResolver = resolve))

/**
 * *(AI-generated)*
 * Returns a promise that resolves when the layer management service is ready.
 */
// eslint-disable-next-line require-await
export const layerManagementReady = async () => readyPromise

/**
 * Sets the service as ready or not ready.
 * @param ready if the service should be marked as ready
 */
export const setLayerManagementReady = (ready: boolean) => {
  if (ready) {
    // Set resolver as ready
    readyResolver()
  } else {
    // Reset the ready promise, so it can be waited on again.
    // This creates a reference to the old resolver, to ensure any old promise
    // listeners won't get dropped.
    const oldResolver = readyResolver
    readyPromise = new Promise((resolve) => (readyResolver = resolve))
    readyPromise.then(oldResolver)
  }
}

/**
 * *(AI-generated)*
 * Initializes the layer management service and loads the initial stylesheet.
 */
export const initializeLMS = async () => {
  resetStore()
  cleanupListeners()
  cleanupWatchers()

  let stylesheet: unknown, apiResponse: unknown

  try {
    const resp = await layerManagementPromises()
    stylesheet = resp[0]
    apiResponse = resp[1]
  } catch (err) {
    // app cannot function without these two assets
    // log error to sentry and throw unrecoverable error
    captureException(err as Error, {
      tags: {
        lms: 'lms assets fetch failed',
      },
    })
    throw new Error('layer management initialization failed')
  }

  const config = await getConfig(stylesheet, apiResponse)

  const layerManager = new LayerManager(config)
  manager = layerManager

  const { api, events } = layerManager

  // Register layer management event listeners
  registerListeners(events)

  // Listen for layer management ready event
  events.once('ready', () => {
    migrateBasemaps()

    performance.mark(INIT_LAYER_MANAGEMENT_STOP)
    setLayerManagementReady(true)
  })

  // Initialize layer management package
  performance.mark(INIT_LAYER_MANAGEMENT_START)

  api.setStorage(getPersistedSettings())

  api.initialize()

  // Register webmap watchers
  registerWatchers(api)
}

const layerManagementPromises = () => {
  const url = getRuntimeResponseUrl()

  const promises = [
    // stylesheet
    initStylesheet(),
    // layer-management runtime response
    httpGetJson(url),
    // has 2 second timeout internally
    // will not reject on timeout
    initializePersistence(),
  ]

  return Promise.all(promises)
}

/**
 * Configuration mapping for layer management service versions.
 * Each app type can have multiple feature flag conditions that determine the version.
 * The first matching condition will be used.
 */
const VERSION_CONFIG = {
  backcountry: [{ flags: [], version: 'v1' }],
  fish: [{ flags: [], version: 'v1' }],
} as const

/**
 * Get layer management runtime URL based on app type and active feature flags.
 */
export const getRuntimeResponseUrl = () => {
  const appType = 'fish'
  const config = VERSION_CONFIG[appType]

  const matchingConfig = config.find(({ flags }) =>
    flags.every((flag) => isActive(flag))
  )
  if (!matchingConfig) {
    throw new Error(
      `No matching layer management configuration found for app type ${appType}`
    )
  }

  return `${window.environment.cartoUrl}/layer-management/${matchingConfig.version}/runtime`
}

/**
 * Gets the lms config for the current vertical.
 */
const getConfig = (stylesheet: unknown, apiResponse: unknown): Config => {
  // FUTURE - inject hunt partial config from hunt layer management wrapper once
  // feature is not behind a feature flag.
  const partialConfig: Partial<Config> = {}

  return {
    apiResponse,
    stylesheet,
    stylesheetCustomSources: ALL_CUSTOM_SOURCES,
    membership: mapMembership(subs.getMembershipLevel()),
    storage: StorageOption.InMemory,
    ...partialConfig,
  } as Config
}

/**
 * Converts webmap membership to a LMS membership.  Will fall back to basic if
 * membership is not recognized.
 *
 * Visible for testing.
 */
export const mapMembership = (membership: string): Membership => {
  switch (membership) {
    case 'basic':
      return Membership.BASIC
    case 'premium':
      return Membership.PREMIUM
    case 'elite':
      return Membership.ELITE
    case 'employee':
      return Membership.ELITE
    case 'trial':
      return mapTrial()
    case 'premium_multi_state':
      return Membership.PREMIUM
    default:
      return Membership.BASIC
  }
}

/**
 * Converts trial level to LMS membership.  Will fall back to basic if trial
 * is not recognized.
 */
const mapTrial = (): Membership => {
  if (subs.isEliteTrial()) {
    return Membership.ELITE
  } else if (subs.isPremiumTrial()) {
    return Membership.PREMIUM
  }

  return Membership.BASIC
}

const cleanup: Callback[] = []

const cleanupWatchers = () => {
  while (cleanup.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    cleanup.shift()!.call(this)
  }
}

/**
 * Watch app information and update LMS on change.
 */
const registerWatchers = (api: Api) => {
  const { storage } = useStore()

  cleanup.push(
    watch(
      computed(() => subs.getMembershipLevel()),
      (membership) => {
        api.updateMembership(mapMembership(membership))
      }
    )
  )

  cleanup.push(
    watch(
      computed(() => subs.getStatesArray()),
      (states) => {
        api.updateStates(states)
      }
    )
  )

  cleanup.push(
    watch(storage, (newStorage) => {
      savePersistedSettings(newStorage)
    })
  )
}

const invoke = (method: Callback) => {
  readyPromise.then(method)
}

/**
 * *(AI-generated)*
 * Shows the given layer IDs on the map.
 */
export const showLayers = (layerIds: string[]) => {
  invoke(() => manager.api.showLayers(layerIds))
}

/**
 * *(AI-generated)*
 * Hides the given layer IDs on the map.
 */
export const hideLayers = (layerIds: string[]) => {
  invoke(() => manager.api.hideLayers(layerIds))
}

/**
 * *(AI-generated)*
 * Sets opacity for the given layer IDs.
 */
export const setLayerOpacities = (layerIds: string[], opacity: number) => {
  invoke(() => manager.api.setLayerOpacities(layerIds, opacity))
}

/**
 * *(AI-generated)*
 * Sets filter expressions for the given layer IDs.
 */
export const setLayerFilters = (filters: Map<string, unknown[]>) => {
  invoke(() => manager.api.setLayerFilters(filters))
}

/**
 * *(AI-generated)*
 * Updates a map source by ID.
 */
export const updateSource = (id: string, newSource: unknown) => {
  invoke(() => manager.api.updateSource(id, newSource as Source))
}

/**
 * *(AI-generated)*
 * Sets the maximum source zoom level.
 */
export const setMaxSourceZoom = (zoom: number) => {
  invoke(() => manager.api.setMaxSourceZoom(zoom))
}

/**
 * *(AI-generated)*
 * Resets the maximum source zoom level to the default.
 */
export const resetMaxSourceZoom = () => {
  invoke(() => manager.api.resetMaxSourceZoom())
}

/**
 * *(AI-generated)*
 * Sets the opacity for a specific user layer.
 */
export const setUserLayerOpacity = (id: string, opacity: number) => {
  invoke(() => manager.api.setUserLayerOpacity(id, opacity))
}

/**
 * *(AI-generated)*
 * Toggles the active state for a specific user layer.
 */
export const toggleUserLayerActive = (id: string, active: boolean) => {
  invoke(() => manager.api.toggleUserLayerActive(id, active))
}

/**
 * *(AI-generated)*
 * Adds an activity filter to the given mode.
 */
export const addActivityToMode = (mode: string, layerId: string) => {
  invoke(() => manager.api.addActivityToCategory(mode, layerId))
}

/**
 * *(AI-generated)*
 * Removes an activity filter from the given mode.
 */
export const removeActivityFromMode = (mode: string, layerId: string) => {
  invoke(() => manager.api.removeActivityFromCategory(mode, layerId))
}

/**
 * *(AI-generated)*
 * Returns the active mode filters for the given mode.
 */
export const getActiveModeFilters = (mode: string) => {
  return manager?.api.getActiveActivityFilters(mode) ?? []
}

/**
 * *(AI-generated)*
 * Adds a difficulty filter to the given mode.
 */
export const addDifficultyToMode = (mode: string, diffId: string) => {
  invoke(() => manager.api.addDifficultyToCategory(mode, diffId))
}

/**
 * *(AI-generated)*
 * Removes a difficulty filter from the given mode.
 */
export const removeDifficultyFromMode = (mode: string, diffId: string) => {
  invoke(() => manager.api.removeDifficultyFromCategory(mode, diffId))
}

/**
 * *(AI-generated)*
 * Returns the active difficulty filters for the given mode.
 */
export const getActiveModeDifficulties = (mode: string) => {
  return manager?.api.getActiveDifficultyFilters(mode)
}

/**
 * *(AI-generated)*
 * Sets user layer tags as active or inactive.
 */
export const setUserLayerTagsActive = (
  tagIds: string[],
  active: boolean,
  shouldDisable = true
) => {
  invoke(() =>
    manager.api.setUserLayerTagsActive(tagIds, active, shouldDisable)
  )
}

/**
 * *(AI-generated)*
 * Enables user layers for the given tag IDs.
 */
export const enableUserLayersForTags = (
  tagIds: string[],
  useDefaults?: boolean
) => {
  invoke(() => manager.api.enableUserLayersForTags(tagIds, useDefaults))
}

/**
 * *(AI-generated)*
 * Resets user layers to defaults for the given tag IDs.
 */
export const resetUserLayersForTags = (tagIds: string[]) => {
  invoke(() => manager.api.resetUserLayersForTags(tagIds))
}

/**
 * *(AI-generated)*
 * Returns the default filter for the given layer ID.
 */
export const getDefaultLayerFilter = (layerId: string) => {
  return manager.api.getDefaultLayerFilter(layerId) || []
}

/**
 * *(AI-generated)*
 * Returns layers matching the given ID prefixes.
 */
export const getLayersByIdPrefix = (idPrefixes: string[]) => {
  return manager.api.getLayersByIdPrefix(idPrefixes)
}

/**
 * *(AI-generated)*
 * Returns the currently active user layer tags.
 */
export const getActiveTags = () => {
  return manager.api.getActiveTags()
}

/**
 * *(AI-generated)*
 * Returns the currently inactive user layer tags.
 */
export const getInactiveTags = () => {
  return manager.api.getInactiveTags()
}

/**
 * *(AI-generated)*
 * Sets the storage records for the layer management service.
 */
export const setStorage = (newStorage: StorageRecords) => {
  manager.api.setStorage(newStorage)
}

// This is a development method to test the hunt layers migration.
// @ts-expect-error unexpected window method
window.resetHuntMigration = async function () {
  saveItem('layers-migrated', false)
  saveItem('layers-activeCollections', [])
  saveItem('layers-activeLayers', [])

  const accountId = await getUserId()
  // clean up the cloud persistence layer settings
  localStorage.removeItem(`onx.hunt.${accountId}.layer-settings`)
  // update your persisted layer settings with some default legacy values
  updateLayerSettings({
    layers: {
      topo: true,
      'dynamic-hillshade': true,
    },
    tags: {},
    activities: {},
    difficulties: {},
  })
}

// @ts-expect-error unexpected window method
// for development purposes in higher environments
window.resetOffroadMigration = async function () {
  saveItem('offroad_lms_migrated', false)

  const accountId = await getUserId()
  // clean up the cloud persistence layer settings
  localStorage.removeItem(`onx.offroad.${accountId}.layer-settings`)
  // reset persisted layer settings to clear migrated difficulties and activities
  updateLayerSettings({
    layers: {},
    tags: {},
    activities: {},
    difficulties: {},
  })
}
